import { DevicesAccess_GetUsersListDocument, DevicesOrderBy, MetricUnit, ReportScheduleRecurrence as Recurrence } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { capitalizeFirstLetter, getInitials } from '@/utils/stringUtils';
import { getMetricFieldAttributes } from '@/utils/attributes/metricField';
import { getUnitsPerMetric } from '@/utils/getUnitsPerMetric';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { AutocompleteController } from '@components/controllers/AutocompleteController';
import { TextFieldController } from '@components/controllers/TextFieldController';
import { TimePickerController } from '@components/controllers/TimePickerController';
import { ALLOWED_OPERATIONAL_STATES } from '@features/reports2/utils/allowedOperationalStates';
import { GET_DEVICES } from '@features/reports2/graphql/queries/getDevices';
import { StepProps } from '@features/reports2/types';
import {
  getCronExpression,
  getDayOfWeekOptions,
  getHoursAndMinutes,
  getNextCronExecutions,
  getRecurrenceOptions,
} from '@features/reports2/utils/schedule';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ExpandMore } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';
import { teal } from '@mui/material/colors';
import { LocalizationProvider } from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import { FC, ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSuspenseQuery } from '@apollo/client';
import { CreateReportFieldValues } from '../../validation';
import { InnerPaper, StepContentBox, StepTitle } from '../common';

const DEVICES_INPUT = {
  orderBy: DevicesOrderBy.NameAsc,
  operationalStates: ALLOWED_OPERATIONAL_STATES,
};

const PREVIEW_COUNT = 5;

const getEmailInitials = (email: string) => email.slice(0, 2).toUpperCase();

// ---------------------------------------------------------------------------
// Summary card — uses InnerPaper
// ---------------------------------------------------------------------------

const SummaryCard: FC<{
  title: ReactNode;
  count: string;
  children: ReactNode;
  expandLabel: ReactNode;
  expanded: boolean;
  onExpand: () => void;
  hasMore: boolean;
}> = ({ title, count, children, expandLabel, expanded, onExpand, hasMore }) => (
  <InnerPaper display="flex" flexDirection="column" gap={1}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography fontWeight="bold">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {count}
      </Typography>
    </Box>
    {children}
    {hasMore && (
      <Button
        size="small"
        variant="outlined"
        endIcon={
          <ExpandMore
            sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
          />
        }
        onClick={onExpand}
        sx={{ mt: 1, fontSize: '0.75rem' }}
      >
        {expandLabel}
      </Button>
    )}
  </InnerPaper>
);

// ---------------------------------------------------------------------------
// Schedule + template-name section
// ---------------------------------------------------------------------------

const ScheduleSection: FC = () => {
  const { _, i18n } = useLingui();
  const { trigger, setValue, ...methods } = useFormContext<CreateReportFieldValues>();
  const formatters = useFormatters();

  const enabled = methods.watch('step4.enabled');
  const recurrenceOptions = getRecurrenceOptions(i18n);
  const dayOfWeekOptions = getDayOfWeekOptions(i18n);
  const recurrence = methods.watch('step4.schedule.recurrence');

  useEffect(() => {
    trigger('step4');
    // `trigger` is guaranteed stable by RHF — excluded from deps intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurrence, enabled]);

  const schedule = methods.watch('step4.schedule');

  const nextCronExpressions = useMemo(() => {
    if (!schedule) return [];
    const { hours, minutes } = getHoursAndMinutes(schedule.time);
    const cronExpression = getCronExpression(schedule.recurrence, {
      daysOfWeek: schedule.daysOfWeek ?? undefined,
      dayOfWeek: schedule.dayOfWeek ?? undefined,
      minutes,
      hours,
    });
    if (!cronExpression) return [];
    return getNextCronExecutions(cronExpression, formatters);
  }, [schedule, formatters]);

  return (
    <Box mt={4}>
      <Typography variant="h6" fontWeight="bold" mb={0.5}>
        <Trans>Periodicidad del reporte</Trans>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        <Trans>¿Desea generar la periodicidad de su reporte?</Trans>
      </Typography>

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box
          component="label"
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ cursor: 'pointer' }}
        >
          <Checkbox
            checked={enabled}
            onChange={() => setValue('step4.enabled', true)}
            disableRipple
            size="small"
          />
          <Typography variant="body2">
            <Trans>Sí</Trans>
          </Typography>
        </Box>
        <Box
          component="label"
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ cursor: 'pointer' }}
        >
          <Checkbox
            checked={!enabled}
            onChange={() => setValue('step4.enabled', false)}
            disableRipple
            size="small"
          />
          <Typography variant="body2">
            <Trans>No</Trans>
          </Typography>
        </Box>
        {enabled && (
          <Chip
            label={<Trans>Periodicidad activada</Trans>}
            size="small"
            sx={{ bgcolor: teal[400], color: 'white' }}
          />
        )}
      </Box>

      {enabled && (
        <InnerPaper sx={{ width: 'fit-content' }}>
          <Box maxWidth={720}>
            {/* Template name/description — shown first when scheduling */}
            <TextFieldController
              control={methods.control}
              name="step4.templateName"
              label={_(msg`Nombre del reporte`)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
            <TextFieldController
              control={methods.control}
              name="step4.templateDescription"
              label={_(msg`Descripción`)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  multiline: true,
                  minRows: 2,
                  maxRows: 6,
                  sx: { mt: 2 },
                },
              }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Recurrence config */}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
              <Typography variant="body2">
                <Trans>Elija la recurrencia con la cual quiere recibir sus reportes:</Trans>
              </Typography>
              <AutocompleteController
                label={_(msg`Recurrencia`)}
                control={methods.control}
                name="step4.schedule.recurrence"
                options={recurrenceOptions.map((o) => o.value)}
                slotProps={{
                  autocomplete: {
                    getOptionLabel(value) {
                      return recurrenceOptions.find((o) => o.value === value)?.label ?? value;
                    },
                    size: 'small',
                    sx: { width: 280 },
                  },
                }}
              />
              <Typography variant="body2">
                <Trans>Ejecutar a las</Trans>
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.locale}>
                <TimePickerController
                  control={methods.control}
                  name="step4.schedule.time"
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 140 },
                      helperText: '',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {recurrence === Recurrence.Weekly && (
              <Box mb={2}>
                <AutocompleteController
                  label={_(msg`Días de la semana`)}
                  control={methods.control}
                  name="step4.schedule.daysOfWeek"
                  defaultValue={[]}
                  options={dayOfWeekOptions.map((o) => o.enumValue)}
                  slotProps={{
                    autocomplete: {
                      multiple: true,
                      getOptionLabel(value) {
                        return (
                          dayOfWeekOptions.find((o) => o.enumValue === value)?.label ?? value
                        );
                      },
                      size: 'small',
                      sx: { width: 300 },
                    },
                  }}
                />
              </Box>
            )}

            {[Recurrence.MonthlyFirstDay, Recurrence.MonthlyLastDay].includes(recurrence) && (
              <Box mb={2}>
                <AutocompleteController
                  label={_(msg`Día de la semana`)}
                  control={methods.control}
                  name="step4.schedule.dayOfWeek"
                  options={dayOfWeekOptions.map((o) => o.enumValue)}
                  slotProps={{
                    textField: { helperText: '' },
                    autocomplete: {
                      getOptionLabel(value) {
                        return (
                          dayOfWeekOptions.find((o) => o.enumValue === value)?.label ?? value
                        );
                      },
                      size: 'small',
                      sx: { width: 240 },
                    },
                  }}
                />
              </Box>
            )}

            {nextCronExpressions.length > 0 && (
              <Box border={1} borderColor="divider" borderRadius={1} p={1.5} mt={1}>
                <Typography variant="body2" mb={1}>
                  <Trans>
                    Con esta configuración, los próximos reportes se generarían en las{' '}
                    <strong>siguientes fechas:</strong>
                  </Trans>
                </Typography>
                {nextCronExpressions.map((execution) => (
                  <Typography key={execution} variant="body2" fontStyle="italic">
                    {capitalizeFirstLetter(execution)}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </InnerPaper>
      )}

      {/* Editing + schedule disabled: fields not shown, but pre-filled value satisfies validation */}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SuspenseComponent: FC<StepProps> = (props) => {
  const { i18n } = useLingui();
  const methods = useFormContext<CreateReportFieldValues>();

  const errors = methods.formState.errors.step4;
  const onFinishEnabled = !errors || Object.values(errors).length === 0;

  // Subscribe only to the form paths actually consumed in this view (S9).
  // ScheduleSection has its own watches for step4.
  const step1Devices = methods.watch('step1.devices');
  const step1Metrics = methods.watch('step1.metrics');
  const step3Emails = methods.watch('step3.emails');
  const step3CreatePdf = methods.watch('step3.createPdf');
  const step3CreateExcel = methods.watch('step3.createExcel');

  const [devicesExpanded, setDevicesExpanded] = useState(false);
  const [metricsExpanded, setMetricsExpanded] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);

  const { data: devicesData } = useSuspenseQuery(GET_DEVICES, {
    variables: { input: DEVICES_INPUT },
  });

  const { data: usersData } = useSuspenseQuery(DevicesAccess_GetUsersListDocument);
  const allUsers = usersData.users;

  const selectedDeviceIds = step1Devices;
  const selectedMetrics = step1Metrics;
  const selectedEmails = step3Emails.map((e) => e.email);

  const selectedDevices = devicesData.devices.filter((d) => selectedDeviceIds.includes(d.id));
  const totalDevices = devicesData.devices.length;

  const formatLabel = (() => {
    if (step3CreatePdf && step3CreateExcel) return <Trans>PDF y Excel</Trans>;
    if (step3CreatePdf) return <Trans>Documento PDF</Trans>;
    if (step3CreateExcel) return <Trans>Excel</Trans>;
    return '-';
  })();

  const emailsWithUsers = selectedEmails.map((email) => {
    const user = allUsers.find((u) => u.email === email);
    return { email, initials: user ? getInitials(user.fullName) : getEmailInitials(email) };
  });

  const visibleAvatars = emailsWithUsers.slice(0, 4);
  const extraAvatars = emailsWithUsers.length - visibleAvatars.length;

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        onFinishEnabled: props.actions.onFinishEnabled && onFinishEnabled,
        onFinish: () => {
          methods.trigger().then((success) => {
            if (success) props.actions.onFinish();
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Resumen General</Trans>}
        description={<Trans>Visualiza el resumen de los parámetros antes configurados.</Trans>}
      />

      {/* Three summary cards */}
      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={2}>
        {/* Devices */}
        <SummaryCard
          title={<Trans>Dispositivos</Trans>}
          count={`${selectedDevices.length}/${totalDevices}`}
          expanded={devicesExpanded}
          onExpand={() => setDevicesExpanded((v) => !v)}
          hasMore={selectedDevices.length > PREVIEW_COUNT}
          expandLabel={
            devicesExpanded ? <Trans>Ver menos</Trans> : <Trans>Ver más dispositivos</Trans>
          }
        >
          {selectedDevices
            .slice(0, devicesExpanded ? undefined : PREVIEW_COUNT)
            .map((d) => (
              <Typography key={d.id} variant="body2">
                {d.profile.name}
              </Typography>
            ))}
        </SummaryCard>

        {/* Metrics */}
        <SummaryCard
          title={<Trans>Métricas seleccionadas</Trans>}
          count={`${selectedMetrics.length}`}
          expanded={metricsExpanded}
          onExpand={() => setMetricsExpanded((v) => !v)}
          hasMore={selectedMetrics.length > PREVIEW_COUNT}
          expandLabel={
            metricsExpanded ? <Trans>Ver menos</Trans> : <Trans>Ver más métricas</Trans>
          }
        >
          {selectedMetrics
            .slice(0, metricsExpanded ? undefined : PREVIEW_COUNT)
            .map((m) => {
              const attrs = getMetricFieldAttributes(m.name, i18n);
              const unitLabel = m.unit && m.unit !== MetricUnit.None
                ? (getUnitsPerMetric(m.name, i18n).find((u) => u.enumValue === m.unit)?.label ??
                  m.unit)
                : '';
              return (
                <Box key={m.name} display="flex" justifyContent="space-between" gap={1}>
                  <Typography variant="body2">● {attrs.dataLabel}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {unitLabel}
                  </Typography>
                </Box>
              );
            })}
        </SummaryCard>

        {/* Recipients */}
        <SummaryCard
          title={<Trans>Destinatarios</Trans>}
          count={`${selectedEmails.length}/${allUsers.length}`}
          expanded={contactsExpanded}
          onExpand={() => setContactsExpanded((v) => !v)}
          hasMore={emailsWithUsers.length > 0}
          expandLabel={
            contactsExpanded ? <Trans>Ver menos</Trans> : <Trans>Ver lista completa</Trans>
          }
        >
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {visibleAvatars.map(({ email, initials }) => (
              <Avatar key={email} sx={{ width: 32, height: 32, fontSize: '0.7rem' }}>
                {initials}
              </Avatar>
            ))}
            {extraAvatars > 0 && (
              <Avatar
                sx={{ width: 32, height: 32, fontSize: '0.7rem', bgcolor: 'primary.main' }}
              >
                +{extraAvatars}
              </Avatar>
            )}
          </Box>
          <Collapse in={contactsExpanded}>
            <Box mt={1}>
              {emailsWithUsers.map(({ email, initials }) => (
                <Box key={email} display="flex" alignItems="center" gap={1} py={0.25}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem' }}>{initials}</Avatar>
                  <Typography variant="caption">{email}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            <Trans>Formato de envío</Trans>
          </Typography>
          <Typography variant="body2">{formatLabel}</Typography>
        </SummaryCard>
      </Box>

      <ScheduleSection />
    </StepContentBox>
  );
};

export const SummaryStep: FC<StepProps> = (props) => {
  return (
    <ApolloErrorBoundary>
      <Suspense>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
